import { trackCall } from "@/lib/track";
import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { MapPin, Phone, Mail, Send } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-background">
      <Toaster theme="dark" position="top-center" />
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-brand-red" />
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Get in touch</span>
          </div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.9]">
            Drop a line.<br /><span className="text-brand-red">Get a real reply.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-px bg-zinc-900 border border-zinc-900">
        <div className="lg:col-span-5 bg-zinc-950 p-10 space-y-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Workshop</div>
            <h3 className="mt-2 font-display uppercase text-3xl text-white tracking-tight">Tassie Automotive HQ</h3>
          </div>

          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-brand-red mt-0.5" />
              <div>
                <div className="text-sm text-zinc-300">46 Grove Rd</div>
                <div className="text-sm text-zinc-300">Glenorchy, Tasmania 7010</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="h-5 w-5 text-brand-red mt-0.5" />
              <a data-testid="contact-phone" href="tel:+61362887861" onClick={() => trackCall("contact_page")} className="text-sm text-zinc-300 hover:text-white">(03) 6288 7861</a>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="h-5 w-5 text-brand-red mt-0.5" />
              <a data-testid="contact-email" href="mailto:hello@tassieautomotive.com.au" className="text-sm text-zinc-300 hover:text-white">hello@tassieautomotive.com.au</a>
            </div>
          </div>

          <div className="border-t border-zinc-900 pt-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Hours</div>
            <ul className="mt-3 space-y-1 text-sm font-mono text-zinc-300">
              <li className="flex justify-between"><span>Mon–Fri</span><span>08:00 – 17:00</span></li>
              <li className="flex justify-between"><span>Saturday</span><span>09:00 – 17:00</span></li>
              <li className="flex justify-between"><span>Sunday</span><span>10:00 – 17:00</span></li>
            </ul>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-7 bg-zinc-950 p-10 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Your name" required>
              <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ta-input" placeholder="Jane Citizen" />
            </Field>
            <Field label="Email" required>
              <input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ta-input" placeholder="you@example.com" />
            </Field>
          </div>
          <Field label="Phone (optional)">
            <input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ta-input" placeholder="0412 345 678" />
          </Field>
          <Field label="Message" required>
            <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="ta-input resize-none" placeholder="Tell us about your car or your question…" />
          </Field>
          <button data-testid="contact-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-3 px-7 py-4 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B] disabled:opacity-60">
            <Send className="h-4 w-4" /> {submitting ? "Sending…" : "Send message"}
          </button>
        </form>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-brand-red" />
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Find us</span>
          </div>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=46+Grove+Rd,+Glenorchy+TAS+7010"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="get-directions-btn"
            onClick={() => {
              if (typeof window !== "undefined" && typeof window.gtag === "function") {
                window.gtag("event", "conversion", {
                  send_to: "AW-17489509584/lWAaCKGajZkcEND50pNB",
                  value: 1.0,
                  currency: "AUD",
                });
              }
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-red text-white font-bold uppercase tracking-widest text-xs hover:bg-[#B91C2B]"
          >
            <MapPin className="h-4 w-4" /> Get directions
          </a>
        </div>
        <div className="border border-zinc-900 bg-zinc-950 p-2">
          <iframe
            data-testid="contact-map"
            title="Tassie Automotive on Google Maps"
            src="https://www.google.com/maps?q=46+Grove+Rd,+Glenorchy+TAS+7010&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-[440px] border-0"
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="contact-seo-copy">
        <div className="border-t border-zinc-900 pt-10 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl">
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">Getting here</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              We're at 46 Grove Road, Glenorchy — just off Main Road, a couple of minutes from Northgate Shopping
              Centre and easy to reach from the Brooker Highway. Free parking is available on site. Coming by bus?
              Most Main Road services stop within a short walk. Drop your car before work and we'll text you the
              moment it's ready.
            </p>
          </div>
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">Who we help</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Tassie Automotive services drivers from Glenorchy, Moonah, Montrose, Rosetta, Berriedale, Claremont,
              New Town and right across greater Hobart. Whether it's a logbook service, brakes, diagnostics, tyres or
              a pre-purchase inspection, call (03) 6288 7861 or use the form above — we usually reply within business
              hours the same day.
            </p>
          </div>
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">When to visit</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              We're open Monday to Friday 8am–5pm, Saturday 9am–5pm and Sunday 10am–5pm. Booked online? Arrive a few
              minutes early, hand over the keys and we'll take it from there — you're welcome to wait, or drop and go
              and we'll text you the moment your car is ready. For urgent same-day jobs, calling is always fastest:
              if a slot has opened up we'll do our best to squeeze you in.
            </p>
          </div>
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">What to bring</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Just your keys and your service logbook if you have it. If a warning light prompted the visit, a quick
              note on when it appears (cold starts, highway speed, under brakes) genuinely helps our diagnosis. Buying
              a used car? Bring the ad or VIN and we'll arrange a pre-purchase inspection so you know exactly what
              you're paying for before money changes hands.
            </p>
          </div>
        </div>
      </section>

      <style>{`
        .ta-input {
          width: 100%;
          background: #0a0a0a;
          border: 1px solid #27272a;
          color: #fafafa;
          padding: 12px 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s;
        }
        .ta-input:focus {
          border-color: #E11D2B;
          box-shadow: 0 0 0 1px #E11D2B;
        }
        .ta-input::placeholder { color: #52525b; }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
      {label} {required && <span className="text-brand-red">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);
